/**
 * AppointmentBillingAPI.js
 *
 * Faturamento de um agendamento com várias linhas (uma por procedimento) e vários
 * pacotes numa chamada só. Separado do API.js principal, no padrão de FiscalAPI.js.
 * Contrato: docs/features/agendamento-multiplos-procedimentos/README.md
 * ("Faturamento" e "Linhas para o formulário de fatura").
 */

import { getAccessToken } from './authTokens';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://www.gabriela.app.br/api/';

const BILLING_LINES_GET = BASE_URL + 'packages/get_appointment_billing_lines.php';
const INVOICE_CREATE    = BASE_URL + 'set_appointment_invoice.php';

const getToken = () => getAccessToken();

/**
 * Linhas do agendamento para o formulário de fatura: uma por procedimento, com o
 * lote de pacote (FEFO) disponível ou `credit: null` quando não há saldo.
 * Resposta: { success, data: { appointment_id, wallet_id, lines: [...] } }.
 * Falha de rede ou endpoint ausente: { success: false, error } (quem chama cai
 * para uma linha avulsa feita do próprio agendamento).
 */
export const GetAppointmentBillingLinesAPI = (appointmentId, callback) => {
    fetch(`${BILLING_LINES_GET}?appointment_id=${encodeURIComponent(appointmentId)}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
        },
    })
        .then(res => res.json())
        .then(data => callback(data))
        .catch(err => callback({ success: false, error: err.message || 'Erro de conexão' }));
};

/**
 * Cria a fatura do agendamento com a origem de cada linha (pacote ou avulso).
 * Mesmo POST form-encoded de SetAppointmentInvoiceAPI (API.js) mais `lines`
 * (JSON). Quando `lines` vem null, o campo não é enviado e vale o POST antigo.
 *
 * O callback recebe o JSON cru do backend mais `httpStatus`:
 *   { status: "SUCCESS", data: { invoiceId, sessions_used, price } }
 *   { status: "ERROR", message } com httpStatus 409 quando um pacote ficou sem saldo.
 */
export const CreateInvoiceWithLinesAPI = (
    { appointmentId, companyId, createdBy, patient, price, paymentMethod, procedure, issueDocument, lines },
    callback
) => {
    const params = new URLSearchParams();
    params.append('appointmentId', appointmentId);
    params.append('companyId', companyId ?? '');
    params.append('patient', patient ?? '');
    params.append('price', price ?? 0);
    params.append('paymentMethod', String(paymentMethod));
    params.append('procedure', procedure ?? '');
    params.append('issueDocument', issueDocument ?? 0);
    params.append('createdBy', createdBy ?? '');
    if (Array.isArray(lines)) params.append('lines', JSON.stringify(lines));

    let httpStatus = 0;
    fetch(INVOICE_CREATE, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
    })
        .then(res => { httpStatus = res.status; return res.text(); })
        .then(text => {
            let data;
            try { data = JSON.parse(text); }
            catch (e) { data = { status: 'ERROR', message: 'Erro ao processar resposta do servidor' }; }
            callback({ ...data, httpStatus });
        })
        .catch(err => callback({ status: 'ERROR', message: err.message || 'Erro de conexão', httpStatus }));
};

// Nomes curtos usados pelo formulário de fatura e pelo popup antigo
export const getBillingLines = GetAppointmentBillingLinesAPI;
export const createInvoiceWithLines = CreateInvoiceWithLinesAPI;
